import React, { createContext, useContext, useState } from "react";
import api, { useExceptionNotification } from "../services/api";
import {
  isSuperAdmin,
  canAccessReport,
  canAccessIntegration,
  canAccessDashboard,
  canAccessProject,
  getUserAccessibleResources,
  hasAnyAccessToResourceType,
  filterResourcesByPermission,
  FEATURE_CODE_TO_KEY,
  ACTION_CODE_TO_KEY,
} from "../helpers/permissions";

const AuthContext = createContext({});
export const project = "@Project";
const DEFAULT_MODULES = ["gestao", "studio"];

const RUN_INCREMENTAL_PERMISSION_NAME = "RUN_INCREMENTAL";
const RUN_INCREMENTAL_PERMISSION_FEATURE = 12;
const RUN_INCREMENTAL_PERMISSION_ACTION = 11;

const normalizePermissionItem = (perm) => {
  if (!perm) return null;
  const name = perm.name ?? perm.Name ?? null;
  const feature = perm.feature ?? perm.Feature ?? null;
  const action = perm.action ?? perm.Action ?? null;
  return {
    name: name != null ? String(name) : null,
    feature:
      feature == null || feature === ""
        ? null
        : Number.isFinite(Number(feature))
          ? Number(feature)
          : null,
    action:
      action == null || action === ""
        ? null
        : Number.isFinite(Number(action))
          ? Number(action)
          : null,
  };
};

const normalizePermissionsList = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizePermissionItem).filter(Boolean);
};

const extractPermissionsFromAuthResponse = (authResponse) => {
  if (!authResponse) return [];
  const responseUser = authResponse?.user ?? authResponse?.User ?? null;
  const token = authResponse?.token ?? authResponse?.Token ?? null;
  const tokenUser =
    token?.user ?? token?.User ?? authResponse?.tokenUser ?? authResponse?.TokenUser ?? null;

  const candidates = [
    responseUser?.permissions,
    responseUser?.Permissions,
    tokenUser?.permissions,
    tokenUser?.Permissions,
    authResponse?.permissions,
    authResponse?.Permissions,
  ];
  const raw = candidates.find((v) => Array.isArray(v)) ?? [];
  return normalizePermissionsList(raw);
};

const mergeUserPermissions = (userObj, authResponse) => {
  const base = userObj ? { ...userObj } : null;
  if (!base) return base;
  const existing = normalizePermissionsList(base.permissions ?? base.Permissions);
  const extracted = extractPermissionsFromAuthResponse(authResponse);
  const next = extracted.length ? extracted : existing;
  return { ...base, permissions: next };
};

const normalizeModulesList = (modules) => {
  if (!Array.isArray(modules)) return null;
  const normalized = modules
    .filter((m) => m != null && String(m).trim())
    .map((m) => String(m).trim().toLowerCase());
  return normalized.length > 0 ? normalized : null;
};

const getModulesFromTenant = (tenant) => {
  if (!tenant) return null;
  return normalizeModulesList(tenant.modulos ?? tenant.Modulos);
};

const getModulesFromAuthResponse = (authResponse) => {
  if (!authResponse) return DEFAULT_MODULES;
  const tenant = authResponse.tenant ?? authResponse.Tenant ?? null;
  return getModulesFromTenant(tenant) ?? DEFAULT_MODULES;
};

// Módulos licenciados por empresa. Todo cliente tem ao menos a licença cortesia de cada um, então o
// menu do módulo aparece sempre; quais empresas usam cada módulo é definido por empresa (Sync.Modulos)
// na tela de Planos, e as telas do módulo só listam essas empresas.
export const LICENSED_MODULES = ["fechamentos", "gestao", "studio"];

const UI_STUDIO_DISABLED = "ui:module:studio:disabled";
const UI_GESTAO_RESTRICTED = "ui:gestao:restricted";
const UI_ACADEMY_DISABLED = "ui:module:academy:disabled";
const UI_FECHAMENTO_DISABLED = "ui:module:fechamento:disabled";
const UI_PLANEJAMENTO_DISABLED = "ui:module:planejamento:disabled";
const UI_FECHAMENTO_READONLY = "ui:module:fechamento:readonly";
const UI_PLANEJAMENTO_READONLY = "ui:module:planejamento:readonly";
const normalizeGestaoMenuKey = (rawKey) => {
  const key = String(rawKey || "").trim().toLowerCase();
  if (key === "paidandreceived") return "payrec";
  return key;
};
const uiGestaoMenuId = (modelKey) =>
  `ui:gestao:${normalizeGestaoMenuKey(modelKey)}`;

export const AuthProvider = ({ children }) => {
  const exceptionNotificationAPI = useExceptionNotification();

  const [user, setUser] = useState(() => {
    const token = localStorage.getItem(`${project}:token`);
    const userData = localStorage.getItem(`${project}:user`);
    const authData = localStorage.getItem(`${project}:authData`);
    const tenantData = localStorage.getItem(`${project}:tenant`);
    const modulesData = localStorage.getItem(`${project}:modules`);

    const userJson = userData ? JSON.parse(userData) : null;
    const tenantJson = tenantData ? JSON.parse(tenantData) : null;
    const authJson = authData ? JSON.parse(authData) : null;
    const modulesJson = modulesData ? JSON.parse(modulesData) : null;
    const storedModules =
      normalizeModulesList(modulesJson) ??
      getModulesFromTenant(tenantJson) ??
      getModulesFromTenant(authJson?.tenant ?? authJson?.Tenant) ??
      DEFAULT_MODULES;

    if (token && userData) {
      api.defaults.headers.authorization = `Bearer ${token}`;
      return {
        token,
        user: userJson,
        authData: authJson,
        tenant: tenantJson,
        modules: storedModules,
      };
    }
    return null;
  });

  const signIn = async ({ username, password, identificador }) => {
    try {
      const response = await api.post("/signin", {
        username,
        password,
        identificador,
      });
      const authResponse = response.data.response ?? response.data.Response;
      const accessToken = authResponse?.accessToken ?? authResponse?.AccessToken;
      const responseUser = authResponse?.user ?? authResponse?.User;
      const responseTenant = authResponse?.tenant ?? authResponse?.Tenant;
      const modules = getModulesFromAuthResponse(authResponse);
      const normalizedUser = mergeUserPermissions(responseUser, authResponse);

      setSignInDataOnLocalStorage(authResponse, modules);
      localStorage.setItem(`${project}:userName`, username);

      setUser({
        token: accessToken,
        user: normalizedUser,
        authData: authResponse,
        tenant: responseTenant,
        modules,
      });
      sessionStorage.setItem("showLoginUpdateNotice", "true");

      api.defaults.headers.authorization = `Bearer ${accessToken}`;

      return response;
    } catch (error) {
      exceptionNotificationAPI(error);
      throw error;
    }
  };

  const signOut = () => {
    api.defaults.headers.authorization = "";
    localStorage.removeItem(`${project}:token`);
    localStorage.removeItem(`${project}:user`);
    localStorage.removeItem(`${project}:tenant`);
    localStorage.removeItem(`${project}:authData`);
    localStorage.removeItem(`${project}:userName`);
    localStorage.removeItem(`${project}:modules`);
    setUser(null);
  };

  const setSignInDataOnLocalStorage = (authResponse, modules) => {
    if (authResponse) {
      const accessToken = authResponse?.accessToken ?? authResponse?.AccessToken;
      const responseUser = mergeUserPermissions(
        authResponse?.user ?? authResponse?.User,
        authResponse,
      );
      const responseTenant = authResponse?.tenant ?? authResponse?.Tenant;
      const responseRoles = authResponse?.roles ?? authResponse?.Roles;
      const modulesToStore = normalizeModulesList(modules) ?? getModulesFromAuthResponse(authResponse);

      // Limpar dados antigos
      sessionStorage.clear();
      localStorage.removeItem(`${project}:token`);
      localStorage.removeItem(`${project}:user`);
      localStorage.removeItem(`${project}:tenant`);
      localStorage.removeItem(`${project}:authData`);
      localStorage.removeItem(`${project}:userName`);
      localStorage.removeItem(`${project}:modules`);

      // Salvar novos dados organizados
      localStorage.setItem(`${project}:token`, accessToken);
      localStorage.setItem(`${project}:user`, JSON.stringify(responseUser));
      localStorage.setItem(`${project}:tenant`, JSON.stringify(responseTenant));
      localStorage.setItem(`${project}:modules`, JSON.stringify(modulesToStore));
      localStorage.setItem(
        `${project}:authData`,
        JSON.stringify({
          id: authResponse.id,
          authenticated: authResponse.authenticated,
          created: authResponse.created,
          expiration: authResponse.expiration,
          tenant: responseTenant,
          roles: responseRoles,
          identity: authResponse.identity,
          modules: modulesToStore,
        }),
      );

      api.defaults.headers.authorization = `Bearer ${accessToken}`;
    }
  };

  // Função principal para verificar permissões
  const can = (feature, actions = []) => {
    if (!user?.user) return false;

    // Super admin tem acesso a tudo
    if (isSuperAdmin(user.user)) return true;

    // Verificar permissões individuais do usuário (novo formato do backend)
    if (user?.user?.permissions && Array.isArray(user.user.permissions)) {
      return user.user.permissions.some((perm) => {
        const permName = perm?.name ?? perm?.Name ?? null;
        if (!permName) return false;

        // Separar action_feature (ex: DELETE_REPORTS)
        const parts = String(permName).split("_");
        if (parts.length < 2) return false;

        const permAction = parts[0].toUpperCase();
        const permFeature = parts.slice(1).join("_").toUpperCase();

        // Se não especificou actions, basta a feature coincidir
        if (actions.length === 0) {
          return permFeature === feature.toUpperCase();
        }

        // Verificar se tem a action específica
        return (
          permFeature === feature.toUpperCase() &&
          actions.map((a) => a.toUpperCase()).includes(permAction)
        );
      });
    }

    return false;
  };

  // Função para verificar permissão específica (compatibilidade)
  const hasPermission = (permission) => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;

    // Verificar nas permissões do usuário
    if (user?.user?.permissions) {
      return user.user.permissions.some(
        (perm) => (perm?.name ?? perm?.Name) === permission,
      );
    }

    return false;
  };

  // Função para verificar se é admin
  const isAdmin = () => {
    return user?.user ? isSuperAdmin(user.user) : false;
  };

  const userHasPermission = ({ name, feature, action }) => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    const perms = Array.isArray(user.user.permissions) ? user.user.permissions : [];

    const targetName = name != null ? String(name) : null;
    const targetFeature =
      feature == null || feature === ""
        ? null
        : Number.isFinite(Number(feature))
          ? Number(feature)
          : null;
    const targetAction =
      action == null || action === ""
        ? null
        : Number.isFinite(Number(action))
          ? Number(action)
          : null;

    if (!targetName && targetFeature == null && targetAction == null) return false;

    return perms.some((p) => {
      const pName = p?.name ?? p?.Name ?? null;
      if (targetName && pName && String(pName) === targetName) return true;
      const pFeature = p?.feature ?? p?.Feature ?? null;
      const pAction = p?.action ?? p?.Action ?? null;
      const pf =
        pFeature == null || pFeature === ""
          ? null
          : Number.isFinite(Number(pFeature))
            ? Number(pFeature)
            : null;
      const pa =
        pAction == null || pAction === ""
          ? null
          : Number.isFinite(Number(pAction))
            ? Number(pAction)
            : null;
      return targetFeature != null && targetAction != null && pf === targetFeature && pa === targetAction;
    });
  };

  const canRunIncremental = () =>
    userHasPermission({
      name: RUN_INCREMENTAL_PERMISSION_NAME,
      feature: RUN_INCREMENTAL_PERMISSION_FEATURE,
      action: RUN_INCREMENTAL_PERMISSION_ACTION,
    });

  // ===========================
  // FUNÇÕES DE PERMISSÕES GRANULARES
  // ===========================

  const canAccessReportById = (reportId) => {
    return canAccessReport(user?.user, reportId);
  };

  const canAccessIntegrationById = (integrationId) => {
    return canAccessIntegration(user?.user, integrationId);
  };

  const canAccessDashboardById = (dashboardId) => {
    return canAccessDashboard(user?.user, dashboardId);
  };

  const canAccessProjectById = (projectId) => {
    return canAccessProject(user?.user, projectId);
  };

  const getAccessibleReports = () => {
    return getUserAccessibleResources(user?.user, "reports");
  };

  const getAccessibleIntegrations = () => {
    return getUserAccessibleResources(user?.user, "integrations");
  };

  const getAccessibleDashboards = () => {
    return getUserAccessibleResources(user?.user, "dashboards");
  };

  const getAccessibleProjects = () => {
    return getUserAccessibleResources(user?.user, "projects");
  };

  // Verificar se tem algum acesso a um tipo de recurso
  const hasAnyReports = () => {
    return hasAnyAccessToResourceType(user?.user, "reports");
  };

  const hasAnyIntegrations = () => {
    return hasAnyAccessToResourceType(user?.user, "integrations");
  };

  const hasAnyDashboards = () => {
    return hasAnyAccessToResourceType(user?.user, "dashboards");
  };

  const hasAnyProjects = () => {
    return hasAnyAccessToResourceType(user?.user, "projects");
  };

  // Função helper para filtrar listas
  const filterByPermission = (
    items,
    resourceType,
    getIdFn = (item) => item.id,
  ) => {
    return filterResourcesByPermission(
      user?.user,
      resourceType,
      items,
      getIdFn,
    );
  };

  // Função para obter informações do usuário
  const getUserInfo = () => {
    if (!user?.user) return null;

    return {
      id: user.user.id,
      username: user.user.username || user.user.userName,
      name: user.user.name,
      email: user.user.email,
      active: user.user.active,
      permissions: user.user.permissions || [],
      granularPermissions: user.user.granularPermissions || [],
      roles: user.user.roles || [],

      created: user.user.created,
    };
  };

  // Função para obter informações do tenant
  const getTenantInfo = () => {
    if (!user?.tenant) return null;

    return {
      id: user.tenant.id,
      nome: user.tenant.nome,
      identificador: user.tenant.identificador,
      ativo: user.user.ativo,
      created: user.user.created,
      franquiaId: user.tenant.franquiaId,
      theme: user.tenant.theme,
      acessoLegado: user.tenant.acessoLegado === true,
      modules: user.modules ?? DEFAULT_MODULES,
    };
  };

  // Função para obter informações de autenticação completas
  const getAuthInfo = () => {
    if (!user?.user) return null;

    return {
      id: user.user.id,
      authenticated: user.user.authenticated,
      created: user.user.created,
      expiration: user.user.expiration,
      roles: user.user.roles || [], // Roles do response principal
      identity: user.user.identity,
    };
  };

  // Função para obter todas as roles (combina roles do user e do authData)
  const getAllRoles = () => {
    const userRoles = user?.user?.roles || [];
    const authRoles = user?.user?.roles || [];

    // Combinar roles removendo duplicatas
    const allRoles = [...userRoles];
    authRoles.forEach((role) => {
      if (
        !allRoles.some((r) => (typeof r === "string" ? r : r.name) === role)
      ) {
        allRoles.push({ name: role });
      }
    });

    return allRoles;
  };

  const modules = user?.modules ?? DEFAULT_MODULES;
  const hasModule = (moduleKey) => {
    const key = String(moduleKey || "").trim().toLowerCase();
    if (!key) return false;
    return modules.includes(key);
  };

  const getGranularIdsByType = (resourceKey) => {
    if (!user?.user) return [];
    if (isSuperAdmin(user.user)) return null;
    const ids = getUserAccessibleResources(user.user, resourceKey);
    return ids;
  };

  const hasUiFlag = (id) => {
    const reportIds = getGranularIdsByType("reports");
    if (reportIds === null) return false;
    return Array.isArray(reportIds) && reportIds.includes(id);
  };

  const hasEffectiveModule = (moduleKey) => {
    const key = String(moduleKey || "").trim().toLowerCase();
    if (!key) return false;
    if (!user?.user) return false;
    // Módulo licenciado vale para todo cliente (licença contratada ou cortesia), independentemente
    // de Tenant.Modulos. As restrições por usuário (flags de UI) continuam valendo.
    if (!LICENSED_MODULES.includes(key) && !hasModule(key)) return false;
    if (isSuperAdmin(user.user)) return true;
    if (key === "studio") {
      return !hasUiFlag(UI_STUDIO_DISABLED);
    }
    return true;
  };

  const canAccessAcademy = () => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    return !hasUiFlag(UI_ACADEMY_DISABLED);
  };

  // Planos: acesso por permissão BILLING (VIEW para consultar, EDIT para alterar tier/módulos).
  const canAccessPlanos = () => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    return can("BILLING", ["VIEW"]);
  };

  const canManagePlanos = () => {
    if (!canAccessPlanos()) return false;
    if (isSuperAdmin(user.user)) return true;
    return can("BILLING", ["EDIT"]);
  };

  // Auditoria financeira: VIEW para consultar os alertas, EDIT para decidir/aprovar correções.
  const canAccessAudit = () => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    return can("AUDIT", ["VIEW"]);
  };

  const canManageAudit = () => {
    if (!canAccessAudit()) return false;
    if (isSuperAdmin(user.user)) return true;
    return can("AUDIT", ["EDIT"]) || can("AUDIT", ["MANAGE"]);
  };

  const canAccessFechamento = () => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    return !hasUiFlag(UI_FECHAMENTO_DISABLED);
  };

  // Planejamento (Simulador do DRE + Metas): módulo do Tenant ("planejamento") + flag de UI por usuário.
  const canAccessPlanejamento = () => {
    if (!user?.user) return false;
    if (!hasModule("planejamento")) return false;
    if (isSuperAdmin(user.user)) return true;
    return !hasUiFlag(UI_PLANEJAMENTO_DISABLED);
  };
  // Planejamento somente leitura: vê simulador, metas e orçamentário, mas não grava cenários nem metas.
  const canManagePlanejamento = () => {
    if (!canAccessPlanejamento()) return false;
    if (isSuperAdmin(user.user)) return true;
    return !hasUiFlag(UI_PLANEJAMENTO_READONLY);
  };
  // Fechamento somente leitura: vê o módulo, mas não cria/edita/exclui.
  const canManageFechamento = () => {
    if (!canAccessFechamento()) return false;
    if (isSuperAdmin(user.user)) return true;
    return !hasUiFlag(UI_FECHAMENTO_READONLY);
  };

  const canAccessGestaoMenu = (modelKey) => {
    if (!user?.user) return false;
    if (isSuperAdmin(user.user)) return true;
    const reportIds = getGranularIdsByType("reports");
    if (reportIds === null) return true;
    const hasRestrictions =
      Array.isArray(reportIds) && reportIds.includes(UI_GESTAO_RESTRICTED);
    if (!hasRestrictions) return true;
    return Array.isArray(reportIds) && reportIds.includes(uiGestaoMenuId(modelKey));
  };

  const hasGestao = hasEffectiveModule("gestao");
  const hasStudio = hasEffectiveModule("studio");

  return (
    <AuthContext.Provider
      value={{
        user: user?.user,
        userRoles: getAllRoles(), // Todas as roles combinadas
        tenant: getTenantInfo(),
        authInfo: getAuthInfo(),
        modules,
        hasModule,
        hasEffectiveModule,
        hasGestao,
        hasStudio,
        canAccessAcademy,
        canAccessPlanos,
        canManagePlanos,
        canAccessAudit,
        canManageAudit,
        canManageFechamento,
        canAccessFechamento,
        canAccessPlanejamento,
        canManagePlanejamento,
        canAccessGestaoMenu,
        signIn,
        signOut,
        can,
        hasPermission,
        userHasPermission,
        canRunIncremental,
        isAdmin,
        // Funções de permissões granulares
        canAccessReportById,
        canAccessIntegrationById,
        canAccessDashboardById,
        canAccessProjectById,
        // Funções para obter listas de recursos acessíveis
        getAccessibleReports,
        getAccessibleIntegrations,
        getAccessibleDashboards,
        getAccessibleProjects,
        // Verificações de existência de recursos
        hasAnyReports,
        hasAnyIntegrations,
        hasAnyDashboards,
        hasAnyProjects,
        // Funções helper
        filterByPermission,
        getUserInfo,
        getTenantInfo,
        getAuthInfo,
        getAllRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
